import { Info, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import { useTrialProductLimit } from '~/features/trial/hooks/use-trial-product-limit';
import { useTrialStore } from '~/store/trial.store';

/**
 * Global status bar at the top of the authenticated shell for accounts that are
 * NOT paying customers. It shows one of two messages depending on contract state:
 *   - a trial message when any product is on a TRIAL_POC contract, or
 *   - a "not enrolled yet" message when the account has no contract.
 * A single Live contract suppresses the bar entirely (paying customer).
 *
 * Dismissal is local state, so it resets on every full page reload (bar
 * reappears) but stays hidden across client-side navigation while the shell
 * stays mounted — matching the "show on every reload" requirement without a
 * persisted store.
 */
export function TrialModeBanner() {
  const { t } = useTranslation('common');
  const enrolledContracts = useTrialStore((s) => s.enrolledContracts);
  const isLoaded = useTrialStore((s) => s.isLoaded);
  // Product cap shown in the trial copy — 20 when trial-max-20 is on, else 500.
  const { limit } = useTrialProductLimit();
  const [dismissed, setDismissed] = useState(false);

  // Validate the contract state first: render nothing until the enrolled-products
  // data has resolved, so nothing flashes before the (initially empty) contract
  // map is populated.
  if (!isLoaded || dismissed) return null;

  const contractTypes = Object.values(enrolledContracts);
  const hasLive = contractTypes.some((ct) => ct === 'LIVE');
  const hasTrial = contractTypes.some((ct) => ct === 'TRIAL_POC');

  // A Live contract means the account is a paying customer — no bar.
  if (hasLive) return null;

  // Trial contract → trial copy; otherwise the account isn't enrolled yet.
  const title = hasTrial ? t('trialBanner.title') : t('trialBanner.notEnrolledTitle');
  const message = hasTrial
    ? t('trialBanner.message', { max: limit })
    : t('trialBanner.notEnrolledMessage');

  return (
    <div className="bg-accent-100 text-accent-main flex items-center justify-between gap-4 px-4 py-2 text-sm">
      <div className="flex items-center gap-2">
        <Info className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          <span className="font-semibold">{title}</span>
          <span className="text-foreground"> — {message}</span>
        </span>
      </div>
      <Button
        variant="ghost"
        size="icon"
        className="text-accent-main hover:bg-accent-main/10 h-7 w-7 shrink-0 cursor-pointer"
        aria-label={t('trialBanner.dismiss')}
        onClick={() => {
          setDismissed(true);
        }}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  );
}
